import { Inline } from '@styleseat/ui';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { BrandColor } from '@styleseat/brand';
import { ProActionButton } from './ProActionButton';

import {
  InfoIcon,
  MapsMapPinLocationIcon,
  MessageIcon,
  StarIcon,
  SymbolsInstagramIcon,
} from '../../../../ui/Icons';
import store from '../../../../../store';
import { RootState } from '../../../../../store/models';
import { CompleteProfileData } from '../../../../pages/ProProfile/ProProfile.model';
import { BodyText } from '../../../../ui';
import analytics from '../../../../../modules/analytics';
import { useIsMobile } from '../../../../../hooks';
import { openExternalUrl } from '../../../../../modules/openExternalUrl';
import { ReviewsActionModal } from './ReviewsActionModal';
import AboutMeModal from './AboutMeModal';
import LocationActionModal from './LocationActionModal';

const HOCRatingIcon = ({ ratings }) => ({
  color, height, width,
}) => (
  <Inline style={{ gap: 4 }} justifyContent="center" alignItems="center">
    <StarIcon color={color} height={height} width={width} />
    {ratings && (<BodyText color={BrandColor.Metal} appearance="small">{ratings.toFixed(1)}</BodyText>)}
  </Inline>
);

export const ProviderActionButtonsContainer = ({
  providerId,
  onMessageClicked,
}) => {
  const isMobile = useIsMobile();
  const [showReviewsActionModal, setShowReviewsActionModal] = useState(false);
  const [showLocationActionModal, setShowLocationActionModal] = useState(false);
  const {
    profile,
  } = useSelector<RootState, CompleteProfileData>(
    store.select.proProfile.getCompleteProfile(providerId),
  ) || {};

  const RatingsIcon = useMemo(
    () => HOCRatingIcon({ ratings: profile?.rating_stars }),
    [profile?.rating_stars],
  );
  const hasBusinessLocation = profile.address1 || profile.city || profile.zipcode;

  const handleLocationActionModalClose = () => {
    setShowLocationActionModal(false);
  };

  const handleReviewsActionModalClose = () => {
    analytics.track('reviews-action-modal-close-button-clicked', {
      pro_id: profile.id,
    });
    setShowReviewsActionModal(false);
  };

  const handleInstagramClick = () => {
    analytics.track(
      'profile_instagram_username_click',
      {
        username: profile?.instagram,
        links_out: false,
        pro_id: providerId,
      },
    );
    openExternalUrl(`https://www.instagram.com/${profile?.instagram}`, '_blank');
  };

  const handleReviewsActionButtonClick = () => {
    analytics.track('reviews-action-modal-button-clicked', {
      pro_id: profile.id,
    });
    setShowReviewsActionModal(true);
  };

  const [showAboutMe, setShowAboutMe] = useState(false);
  const handleAboutMeModalClose = () => {
    analytics.track('about-me-modal-close-button-clicked', {
      pro_id: profile.id,
    });
    setShowAboutMe(false);
  };
  const handleAboutButtonClick = () => {
    analytics.track('pro-action-buttons-container-about-button-clicked', {
      pro_id: profile.id,
    });
    setShowAboutMe(true);
  };

  const handleLocationActionButtonClick = () => {
    analytics.track('location-action-modal-button-clicked', {
      pro_id: profile.id,
    });
    setShowLocationActionModal(true);
  };

  const handleCallProClicked = () => {
    analytics.track('about-me-modal-call-pro-clicked', {
      pro_id: profile.id,
    });
    openExternalUrl(`tel:${profile.public_phone_number}`);
  };

  const handleOnMessageClicked = () => {
    analytics.track('provider_profile_revamp_message_button_clicked', {
      profile_type: 'provider',
      provider_id: profile.id,
    });

    onMessageClicked();
  };

  return (
    <Inline
      overflowX={isMobile ? 'scroll' : null}
      width="100%"
      paddingBottom={6}
      paddingRight={24}
      style={{ gap: 6 }}
    >
      <ProActionButton
        text="Message"
        ButtonIconComponent={MessageIcon}
        onPress={handleOnMessageClicked}
        testID="pro-action-button-message"
      />
      {isMobile
      && hasBusinessLocation
      && (
        <ProActionButton
          text="Location"
          testID="pro-action-button-location"
          ButtonIconComponent={MapsMapPinLocationIcon}
          onPress={handleLocationActionButtonClick}
        />
      )}
      <ProActionButton
        text="Reviews"
        ButtonIconComponent={RatingsIcon}
        onPress={handleReviewsActionButtonClick}
        testID="pro-action-button-reviews"
      />
      {!!profile?.instagram && (
        <ProActionButton
          text="Instagram"
          ButtonIconComponent={SymbolsInstagramIcon}
          onPress={handleInstagramClick}
          testID="pro-action-button-instagram"
        />
      )}
      {isMobile && (
        <React.Fragment>
          <ProActionButton
            text="About"
            ButtonIconComponent={InfoIcon}
            onPress={handleAboutButtonClick}
            testID="pro-action-button-about"
          />
          {showLocationActionModal && (
            <LocationActionModal
              onClose={handleLocationActionModalClose}
              profile={profile}
              onMessageClicked={onMessageClicked}
            />
          )}
          {showAboutMe && (
            <AboutMeModal
              onClose={handleAboutMeModalClose}
              profile={profile}
              onMessagePro={onMessageClicked}
              onCallPro={handleCallProClicked}
            />
          )}
        </React.Fragment>
      )}
      {showReviewsActionModal && (
        <ReviewsActionModal
          onClose={handleReviewsActionModalClose}
          profile={profile}
        />
      )}
    </Inline>
  );
};
